import { useState, useEffect } from 'react';
import { ChevronLeftIcon, PlusIcon, LocationIcon } from '../components/Icons';
import { toast } from '../toast';
import ConfirmModal from '../components/ConfirmModal';

const NG_STATES = [
  'Abia','Adamawa','Akwa Ibom','Anambra','Bauchi','Bayelsa','Benue','Borno',
  'Cross River','Delta','Ebonyi','Edo','Ekiti','Enugu','FCT - Abuja','Gombe',
  'Imo','Jigawa','Kaduna','Kano','Katsina','Kebbi','Kogi','Kwara','Lagos',
  'Nasarawa','Niger','Ogun','Ondo','Osun','Oyo','Plateau','Rivers','Sokoto',
  'Taraba','Yobe','Zamfara',
];

export default function AddressesPage({ onNavigate, user }) {
  const storageKey = `mb_addresses_${user?.id || 'guest'}`;

  const [addresses, setAddresses] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error(e);
      }
    }
    return [];
  });

  const [isEditing, setIsEditing] = useState(false);
  const [currentAddress, setCurrentAddress] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    addressLine: '',
    city: '',
    state: '',
    zipCode: '',
    isDefault: false,
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(addresses));
  }, [addresses, storageKey]);

  const handleOpenAdd = () => {
    setCurrentAddress(null);
    setFormData({
      fullName: '',
      phone: '',
      addressLine: '',
      city: '',
      state: '',
      zipCode: '',
      isDefault: addresses.length === 0,
    });
    setIsEditing(true);
  };

  const handleOpenEdit = (address) => {
    setCurrentAddress(address);
    setFormData({ ...address });
    setIsEditing(true);
  };

  const requestDelete = (id) => {
    setAddressToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    const id = addressToDelete;
    const updated = addresses.filter((addr) => addr.id !== id);
    if (addresses.find((addr) => addr.id === id)?.isDefault && updated.length > 0) {
      updated[0].isDefault = true;
    }
    setAddresses(updated);
    toast.success('Address deleted successfully');
    setShowDeleteModal(false);
    setAddressToDelete(null);
  };

  const handleSetDefault = (id) => {
    const updated = addresses.map((addr) => ({
      ...addr,
      isDefault: addr.id === id,
    }));
    setAddresses(updated);
    toast.success('Default address updated');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.fullName || !formData.phone || !formData.addressLine || !formData.city || !formData.state) {
      toast.error('Please fill in all required fields.');
      return;
    }

    let updated;
    if (currentAddress) {
      updated = addresses.map((addr) => {
        if (addr.id === currentAddress.id) {
          return { ...addr, ...formData };
        }
        return formData.isDefault ? { ...addr, isDefault: false } : addr;
      });
      toast.success('Address updated successfully');
    } else {
      const newAddress = {
        ...formData,
        id: Date.now().toString(),
      };
      if (formData.isDefault) {
        updated = addresses.map((addr) => ({ ...addr, isDefault: false }));
        updated.push(newAddress);
      } else {
        updated = [...addresses, newAddress];
      }
      toast.success('Address added successfully');
    }

    setAddresses(updated);
    setIsEditing(false);
  };

  return (
    <div className="min-h-screen bg-[#FAF3F0]">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 lg:px-8 pt-5 pb-4 bg-[#FAF3F0]">
        <button onClick={() => onNavigate('account')} className="p-1.5 -ml-1.5 text-[#F47B20]">
          <ChevronLeftIcon className="w-6 h-6" />
        </button>
      </div>

      <div className="px-4 lg:px-8 pb-28 lg:pb-12 max-w-2xl lg:max-w-4xl">
        {!isEditing ? (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-lg font-bold text-[#1A1A2E] mb-1">Your Addresses</h2>
                <p className="text-gray-400 text-sm">Manage where you receive your orders.</p>
              </div>
              <button
                onClick={handleOpenAdd}
                className="bg-[#F47B20] hover:bg-[#D96A10] text-white px-4 py-2.5 rounded-xl text-sm font-bold flex items-center gap-1.5 shadow-sm transition-all whitespace-nowrap"
              >
                <PlusIcon className="w-4 h-4 text-white flex-shrink-0" />
                <span>Add New</span>
              </button>
            </div>

            {addresses.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-gray-100 shadow-sm flex flex-col items-center">
                <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mb-4">
                  <LocationIcon className="w-8 h-8 text-[#F47B20]" />
                </div>
                <p className="font-bold text-[#1A1A2E] text-lg mb-1">No addresses saved yet</p>
                <p className="text-gray-400 text-sm max-w-xs mb-6">Please add a shipping address to speed up checkout.</p>
                <button
                  onClick={handleOpenAdd}
                  className="bg-[#1A1A2E] hover:bg-black text-white px-6 py-3 rounded-xl text-sm font-bold shadow-md"
                >
                  Create Address
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {addresses.map((address) => (
                  <div
                    key={address.id}
                    className={`bg-white rounded-2xl p-5 border shadow-sm transition-all relative ${
                      address.isDefault ? 'border-[#F47B20] ring-1 ring-[#F47B20]/20' : 'border-gray-100'
                    }`}
                  >
                    {address.isDefault && (
                      <span className="absolute top-4 right-4 bg-[#FEF0E6] text-[#F47B20] text-xs font-bold px-2.5 py-1 rounded-full">
                        Default
                      </span>
                    )}

                    <h3 className="font-bold text-[#1A1A2E] text-base mb-1">{address.fullName}</h3>
                    <p className="text-gray-500 text-sm mb-3">{address.phone}</p>
                    <p className="text-[#1A1A2E] text-sm leading-relaxed mb-4">
                      {address.addressLine}, {address.city}, {address.state} {address.zipCode && `(${address.zipCode})`}
                    </p>

                    <div className="flex items-center gap-3 border-t border-gray-100 pt-4 mt-2">
                      <button
                        onClick={() => handleOpenEdit(address)}
                        className="text-xs font-bold text-gray-500 hover:text-[#F47B20] transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => requestDelete(address.id)}
                        className="text-xs font-bold text-gray-400 hover:text-red-500 transition-colors"
                      >
                        Delete
                      </button>
                      {!address.isDefault && (
                        <button
                          onClick={() => handleSetDefault(address.id)}
                          className="ml-auto text-xs font-bold text-[#F47B20] hover:underline"
                        >
                          Set as Default
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-3xl p-6 md:p-8 border border-gray-100 shadow-sm">
            <h2 className="text-lg font-bold text-[#1A1A2E] mb-6">
              {currentAddress ? 'Edit Address' : 'Add New Address'}
            </h2>

            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative md:col-span-2">
                <label className="absolute left-4 top-2 text-xs text-gray-400 z-10">Full Name *</label>
                <input
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  placeholder="Your full name"
                  className="w-full px-4 pt-7 pb-3 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] outline-none bg-white focus:border-[#F47B20] focus:ring-2 focus:ring-[#F47B20]/10 transition-all"
                />
              </div>

              <div className="relative md:col-span-2">
                <label className="absolute left-4 top-2 text-xs text-gray-400 z-10">Phone Number *</label>
                <input 
                  type="tel" 
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  onFocus={(e) => { if (!e.target.value) setFormData({ ...formData, phone: '+234 ' }); }}
                  onBlur={(e) => { if (e.target.value.trim() === '+234') setFormData({ ...formData, phone: '' }); }}
                  placeholder="+234 80..." 
                  className="w-full px-4 pt-7 pb-3 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] outline-none bg-white focus:border-[#F47B20] focus:ring-2 focus:ring-[#F47B20]/10 transition-all" 
                />
              </div>

              <div className="relative md:col-span-2">
                <label className="absolute left-4 top-2 text-xs text-gray-400 z-10">Street Address *</label>
                <input
                  type="text"
                  required
                  value={formData.addressLine}
                  onChange={(e) => setFormData({ ...formData, addressLine: e.target.value })}
                  placeholder="Street address"
                  className="w-full px-4 pt-7 pb-3 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] outline-none bg-white focus:border-[#F47B20] focus:ring-2 focus:ring-[#F47B20]/10 transition-all"
                />
              </div>

              <div className="relative">
                <label className="absolute left-4 top-2 text-xs text-gray-400 z-10">City *</label>
                <input
                  type="text"
                  required
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="City"
                  className="w-full px-4 pt-7 pb-3 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] outline-none bg-white focus:border-[#F47B20] focus:ring-2 focus:ring-[#F47B20]/10 transition-all"
                />
              </div>
              <div className="relative">
                <label className="absolute left-4 top-2 text-xs text-gray-400 z-10">State *</label>
                <select
                  required
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="w-full px-4 pt-7 pb-3 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] outline-none bg-white focus:border-[#F47B20] focus:ring-2 focus:ring-[#F47B20]/10 transition-all appearance-none cursor-pointer"
                >
                  <option value="">Select state…</option>
                  {NG_STATES.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              <div className="relative md:col-span-2">
                <label className="absolute left-4 top-2 text-xs text-gray-400 z-10">ZIP / Postal Code</label>
                <input
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                  placeholder="ZIP / Postal Code"
                  className="w-full px-4 pt-7 pb-3 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] outline-none bg-white focus:border-[#F47B20] focus:ring-2 focus:ring-[#F47B20]/10 transition-all"
                />
              </div>

              <div className="flex items-center gap-2.5 pt-2 md:col-span-2">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  className="w-4 h-4 rounded text-[#F47B20] focus:ring-[#F47B20]/30 border-gray-300"
                />
                <label htmlFor="isDefault" className="text-sm text-[#1A1A2E] font-medium select-none">
                  Set as default shipping address
                </label>
              </div>

              <div className="flex gap-3 pt-4 md:col-span-2">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="flex-1 py-3.5 rounded-xl border border-gray-200 text-[#1A1A2E] text-sm font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3.5 rounded-xl bg-[#F47B20] hover:bg-[#D96A10] text-white text-sm font-bold transition-colors shadow-sm"
                >
                  Save Address
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Address"
        message="Are you sure you want to delete this address? This action cannot be undone."
        confirmText="Delete"
      />
    </div>
  );
}
